import { Injectable } from '@angular/core';
import { WorkspaceApiService } from '@selfai-platform/bi-api';
import { CachedStream, ClearCache, GlobalConfigManager, KD_DEFAULT_CONFIG } from '@selfai-platform/shared';
import { Observable, filter, map, tap } from 'rxjs';
import { RecycleBinApiToDomainService } from '../../recycle-bin';
import { Workspace } from '../models';
import { WorkspaceStore } from '../stores';

@Injectable({ providedIn: 'root' })
export class WorkspaceDomainService {
  constructor(
    private readonly recycleBinApiToDomainService: RecycleBinApiToDomainService,
    private readonly workspaceStore: WorkspaceStore,
    private readonly workspaceApiService: WorkspaceApiService,
  ) {}

  @CachedStream({
    ttl: () =>
      GlobalConfigManager.tryGetInstance().getConfig()?.knowledgeDesigner?.cache?.workspace ??
      KD_DEFAULT_CONFIG.cache.workspace,
    keyExtractor: (...args: unknown[]) => args[0] as string,
  })
  loadWorkspace(workspaceId: string): Observable<Workspace> {
    return this.workspaceStore.getByKey(workspaceId);
  }

  getWorkspace(workspaceId: string): Observable<Workspace> {
    return this.workspaceStore.entityMap$.pipe(
      map((entityMap) => entityMap?.[workspaceId]),
      filter(Boolean),
    );
  }

  getWorkspaceLoading(): Observable<boolean> {
    return this.workspaceStore.loading$;
  }

  getWorkspaceError(): Observable<unknown> {
    return this.workspaceStore.errors$;
  }

  @ClearCache('loadWorkspace', (...args: unknown[]) => args[0] as string)
  updateWorkspace(
    workspaceId: string,
    workspace: Pick<Workspace, 'name' | 'description' | 'externalId'>,
  ): Observable<Workspace> {
    return this.getWorkspace(workspaceId).pipe(
      map((currentWorkspace) => ({ ...currentWorkspace, ...workspace, id: workspaceId })),
      tap((updatedWorkspace) => {
        this.workspaceStore.update(updatedWorkspace);
      }),
    );
  }

  removeToTrash(id: string): Observable<unknown> {
    return this.recycleBinApiToDomainService.recycleBinWorkspace(id);
  }

  /**
   * @deprecated use loadWorkspace instead
   */
  loadWorkspaceWithPermission(id: string): Observable<Workspace> {
    return this.workspaceStore.getByKey(id);
  }

  downloadMigrationArtifact(workspaceId: string, fileName = `migration-${workspaceId}.zip`): Observable<void> {
    return this.workspaceApiService.downloadMigrationArtifact(workspaceId, fileName);
  }
}
